import classnames from 'classnames';
import { SurveyQuestionType } from '@posthog/browser-common';
import type { RatingSurveyQuestion, Survey } from 'posthog-js';

import type { QuestionRendererProps } from './registry';

import styles from './RatingQuestion.module.less';

type Props = QuestionRendererProps & {
  /**
   * Whether to render the question's `lowerBoundLabel` / `upperBoundLabel`
   * beneath the scale when present. Defaults to `true`. Set to `false` in
   * compact contexts (e.g. corner popups) where the labels would clutter.
   */
  shouldShowRangeLabels?: boolean;
};

const isRatingQuestion = (
  q: Survey['questions'][number],
): q is RatingSurveyQuestion => q.type === SurveyQuestionType.Rating;

/**
 * Renderer for PostHog `RatingSurveyQuestion`. Renders a horizontal row of
 * numeric buttons (1..`scale`) honoring the question's `scale` (3/5/7/10).
 * Stores the selected value as a string, matching PostHog's response shape.
 * `display: 'emoji'` falls back to numeric rendering.
 */
export const RatingQuestion = ({
  question, value, onChange, shouldShowRangeLabels = true,
}: Props) => {
  if (!isRatingQuestion(question)) {
    return null;
  }
  return (
    <RatingQuestionContent
      question={question}
      value={value}
      onChange={onChange}
      shouldShowRangeLabels={shouldShowRangeLabels}
    />
  );
};

type ContentProps = Props & { question: RatingSurveyQuestion };

const RatingQuestionContent = ({
  question, value, onChange, shouldShowRangeLabels,
}: ContentProps) => {
  const selected = Array.isArray(value) ? undefined : value;
  const numbers = Array.from({ length: question.scale }, (_, i) => i + 1);
  const hasBoundLabels = !!question.lowerBoundLabel || !!question.upperBoundLabel;
  const shouldRenderBoundLabels = shouldShowRangeLabels && hasBoundLabels;

  return (
    <div className={styles.rating}>
      <div className={styles.questionText}>{question.question}</div>
      <div
        className={styles.scale}
        role="radiogroup"
        aria-label={question.question}
      >
        {numbers.map((n) => {
          const stringValue = String(n);
          const isSelected = selected === stringValue;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={classnames(styles.option, { [styles.selected]: isSelected })}
              onClick={() => onChange(stringValue)}
            >
              {n}
            </button>
          );
        })}
      </div>
      {shouldRenderBoundLabels && (
        <div className={styles.boundLabels}>
          <span>{question.lowerBoundLabel}</span>
          <span>{question.upperBoundLabel}</span>
        </div>
      )}
    </div>
  );
};
